import {Trans, useTranslation} from '@/translations/i18n'
import {ProjectExtended} from '@/features/projects/types'
import {MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {useDialogStore} from '@ariakit/react'
import {FormProvider, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import React from 'react'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {SuspendProjectFormValidationSchema} from '../../formSchemas'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {SlashCircle01Icon} from '@/components/ui/icon/Icon'
import {ProjectOptionsSuspendFields} from '../project-options-suspend-fields/ProjectOptionsSuspendFields'
import {useEditProjectDetails} from '../../queries/useEditProjectDetails'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import dayjs from 'dayjs'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
interface ProjectOptionsSuspendDialogProps {
    project: ProjectExtended
}

export const ProjectOptionsSuspendDialog = ({project}: ProjectOptionsSuspendDialogProps) => {
    const {t} = useTranslation()
    const form = useForm<SuspendProjectFormValidationSchema>({
        resolver: zodResolver(SuspendProjectFormValidationSchema)
    })
    const dialogStore = useDialogStore({
        setOpen: () => {
            form.reset()
        }
    })

    const queryClient = useQueryClient()

    const {mutate: editProjectDetails, isPending: isEditing} = useEditProjectDetails({
        urlParams: {projectId: project.id},
        options: {
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USER_AVAILABILITY]})
                dialogStore.setOpen(false)
            }
        }
    })

    const handleConfirm = form.handleSubmit(data => {
        editProjectDetails({
            suspensionStartDate: dayjs(data.suspensionStartDate).format('YYYY-MM-DD'),
            suspensionEndDate: data.suspensionEndDate ? dayjs(data.suspensionEndDate).format('YYYY-MM-DD') : undefined,
            suspensionReason: data.suspensionReason,
            notificationRecipientIds: data.notificationsRecipients?.map(recipient => recipient.id),
            notificationDetails: data.notificationDetails,
            suspensionNotes: data.suspensionNotes
        })
    })

    return (
        <>
            <MenuItem
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault()
                    e.stopPropagation()
                    dialogStore.setOpen(true)
                }}
            >
                <SlashCircle01Icon />
                {t('projects:change_status_suspend_project')}
            </MenuItem>
            <FormProvider {...form}>
                <ConfirmationDialog
                    dialogStore={dialogStore}
                    onConfirm={handleConfirm}
                    isConfirmPending={isEditing}
                    headerSlot={
                        <Flexbox direction="column" gap={7} fullWidth>
                            <Flexbox direction="column" fullWidth gap={2}>
                                <DialogHeading>{t('projects:change_status_suspend_project')}</DialogHeading>
                                <Flexbox direction="column" fullWidth gap={1}>
                                    {project.patient && project && (
                                        <Text palette="neutral" shade="950" size="textMd">
                                            <Trans
                                                i18nKey="agenda:pri_n_expiration_date"
                                                values={{
                                                    code: project.code || '-',
                                                    date: project.expirationDate
                                                        ? dayjs(project.expirationDate).format('DD/MM/YYYY')
                                                        : '-'
                                                }}
                                            />
                                        </Text>
                                    )}
                                    <Text palette="neutral" shade="600" size="textSm">
                                        {t('commons:patient_fullname_value', {fullName: project.patient?.fullName})}
                                    </Text>
                                </Flexbox>
                            </Flexbox>
                        </Flexbox>
                    }
                    contentSlot={<ProjectOptionsSuspendFields />}
                />
            </FormProvider>
        </>
    )
}
